import { type Chat, type Message } from '@pubnub/chat';
import { useEffect } from 'react';
import { useConversationsStore } from '@avanta/shared/stores/chat/conversations';
import { useNotificationsStore } from '@avanta/shared/stores/chat/notifications';

interface UseUnreadCountsParams {
  channelIds: string[];
  activeChannelId: string | null;
  chat: Chat | null;
}

export const useUnreadCounts = ({
  channelIds,
  activeChannelId,
  chat,
}: UseUnreadCountsParams): void => {
  useEffect(() => {
    if (!chat) return;

    const idsToWatch = channelIds.filter(channelId => channelId !== activeChannelId);
    if (idsToWatch.length === 0) return;

    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    const setup = async (): Promise<void> => {
      const connectionTimetoken = String(Date.now() * 10000);

      await Promise.allSettled(
        idsToWatch.map(async channelId => {
          const channel = await chat.getChannel(channelId);
          if (!channel || cancelled) return;

          const unsub = channel.connect((message: Message) => {
            if (cancelled) return;
            const { updateUnreadCount } = useConversationsStore.getState();
            const { playNotificationSound } = useNotificationsStore.getState();

            const isNewMessage = String(message.timetoken) > connectionTimetoken;
            const isFromOtherUser = message.userId !== chat.currentUser.id;

            if (isFromOtherUser && isNewMessage) {
              updateUnreadCount(channelId, 1, String(message.timetoken));
              playNotificationSound();
            }
          });
          unsubscribers.push(unsub);
        })
      );
    };

    setup();

    return (): void => {
      cancelled = true;
      unsubscribers.forEach(unsub => unsub());
    };
  }, [channelIds, activeChannelId, chat]);
};
